import React, { memo, useId } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { monoSx } from '../dashboard/styles';
import {
  CHILD_NODES,
  CHILD_Y,
  ChildKey,
  EDGES,
  ENTITY_LIST,
  Entity,
  MAP_H,
  MAP_W,
  SelectHandler,
  Selection,
  childEdgePath,
  pct,
  relatedIds,
} from './entityModel';

const LEGEND = [
  ['1', 'exactly one'],
  ['1..*', 'one or more'],
  ['0..*', 'optional'],
] as const;

const Legend: React.FC = () => (
  <Box sx={{ ml: 'auto', display: 'flex', gap: 1.75, flexWrap: 'wrap', fontSize: 12, color: 'text.secondary' }}>
    {LEGEND.map(([card, meaning]) => (
      <Box key={card} component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box component="span" sx={{ ...monoSx, fontWeight: 600, color: 'text.primary' }}>
          {card}
        </Box>
        {meaning}
      </Box>
    ))}
  </Box>
);

const EdgeLabel: React.FC<{ x: number; y: number; text: string; active: boolean }> = ({ x, y, text, active }) => (
  <Box
    component="span"
    sx={{
      ...monoSx,
      position: 'absolute',
      left: pct(x, MAP_W),
      top: pct(y, MAP_H),
      transform: 'translate(-50%, -50%)',
      px: 0.75,
      py: 0.25,
      borderRadius: 1.5,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: active ? 'primary.main' : 'divider',
      fontSize: 11,
      fontWeight: 600,
      color: active ? 'brand.onContainer' : 'text.secondary',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    }}
  >
    {text}
  </Box>
);

const nodeBaseSx = {
  position: 'absolute',
  transform: 'translate(-50%, -50%)',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  textAlign: 'left',
  font: 'inherit',
  transition: 'opacity .15s, box-shadow .15s, background-color .15s',
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
} as const;

interface EntityNodeProps {
  entity: Entity;
  selected: boolean;
  near: boolean;
  onSelect: SelectHandler;
}

const EntityNode: React.FC<EntityNodeProps> = ({ entity, selected, near, onSelect }) => {
  const { id, Icon, color, label, sub } = entity;
  const big = id === 'fc';
  const iconSize = big ? 36 : 30;
  return (
    <ButtonBase
      onClick={() => onSelect(id)}
      aria-pressed={selected}
      aria-label={`${label}: ${sub}`}
      sx={(theme: Theme) => {
        const main = theme.palette[color].main;
        return {
          ...nodeBaseSx,
          left: pct(entity.x, MAP_W),
          top: pct(entity.y, MAP_H),
          width: big ? '20%' : '16%',
          height: big ? '15%' : '11%',
          px: 1.25,
          justifyContent: 'flex-start',
          borderRadius: 3.5,
          border: selected ? `2px solid ${main}` : `1.5px solid ${near ? main : theme.palette.divider}`,
          bgcolor: selected ? main : 'background.paper',
          color: selected ? theme.palette[color].contrastText : 'text.primary',
          boxShadow: selected ? `0 8px 24px ${alpha(main, 0.35)}` : '0 1px 2px rgba(0,21,41,.06)',
          opacity: near ? 1 : 0.55,
        };
      }}
    >
      <Box
        component="span"
        sx={(theme: Theme) => ({
          width: iconSize,
          height: iconSize,
          flexShrink: 0,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: selected ? 'rgba(255,255,255,.18)' : alpha(theme.palette[color].main, 0.12),
          color: selected ? 'inherit' : `${color}.main`,
        })}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
        <Typography component="span" sx={{ fontWeight: 600, fontSize: big ? 15 : 13, lineHeight: 1.2, color: 'inherit' }}>
          {label}
        </Typography>
        <Typography component="span" sx={{ fontSize: 11, lineHeight: 1.2, color: selected ? 'rgba(255,255,255,.85)' : 'text.secondary' }}>
          {sub}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

interface ChildNodeButtonProps {
  childKey: ChildKey;
  label: string;
  sub: string;
  x: number;
  active: boolean;
  focused: boolean;
  onSelect: SelectHandler;
}

const ChildNodeButton: React.FC<ChildNodeButtonProps> = ({ childKey, label, sub, x, active, focused, onSelect }) => (
  <ButtonBase
    onClick={() => onSelect('fc', childKey)}
    aria-pressed={focused}
    aria-label={`${label}: ${sub}`}
    sx={(theme: Theme) => ({
      ...nodeBaseSx,
      left: pct(x, MAP_W),
      top: pct(CHILD_Y, MAP_H),
      width: '12%',
      height: '10%',
      px: 0.75,
      justifyContent: 'center',
      borderRadius: 3,
      border: focused ? `2px solid ${theme.palette.primary.main}` : `1px solid ${active ? alpha(theme.palette.primary.main, 0.45) : theme.palette.divider}`,
      bgcolor: focused ? 'brand.container' : 'background.paper',
      color: 'text.primary',
      opacity: active ? 1 : 0.55,
    })}
  >
    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
      <Typography component="span" sx={{ fontWeight: 600, fontSize: 12, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography component="span" sx={{ fontSize: 11, lineHeight: 1.2, color: 'text.secondary' }}>
        {sub}
      </Typography>
    </Box>
  </ButtonBase>
);

interface EntityMapProps {
  selection: Selection;
  onSelect: SelectHandler;
}

/**
 * Interactive entity-relationship diagram. Geometry lives in a fixed 1000×520 coordinate space;
 * the SVG scales with the box and HTML nodes are positioned by percentage so both stay aligned.
 */
const EntityMap: React.FC<EntityMapProps> = ({ selection, onSelect }) => {
  const theme = useTheme();
  // useId output contains colons, which aren't safe inside url(#…) references.
  const markerId = `arch${useId().replace(/:/g, '')}`;
  const arrowOff = `${markerId}-off`;
  const arrowOn = `${markerId}-on`;
  const related = relatedIds(selection.id);
  const childrenActive = selection.id === 'fc';

  const onStroke = theme.palette.primary.main;
  const offStroke = alpha(theme.palette.text.primary, 0.2);

  return (
    <Paper variant="outlined" component="section" aria-label="Entity map" sx={{ flex: '1 1 640px', minWidth: 0, borderRadius: 4, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2.5, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
          Entity map
        </Typography>
        <Legend />
      </Box>
      <Box sx={{ overflowX: 'auto', p: 2 }}>
        <Box sx={{ position: 'relative', width: '100%', minWidth: 900, aspectRatio: `${MAP_W} / ${MAP_H}` }}>
          <Box
            component="svg"
            viewBox={`0 0 ${MAP_W} ${MAP_H}`}
            aria-hidden
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}
          >
            <defs>
              <marker id={arrowOff} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill={offStroke} />
              </marker>
              <marker id={arrowOn} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill={onStroke} />
              </marker>
            </defs>
            {EDGES.map((e) => {
              const on = e.from === selection.id || e.to === selection.id;
              return (
                <path
                  key={`${e.from}-${e.to}`}
                  d={e.path}
                  fill="none"
                  stroke={on ? onStroke : offStroke}
                  strokeWidth={on ? 2.5 : 1.5}
                  strokeDasharray={e.dashed ? '6 5' : undefined}
                  markerEnd={`url(#${on ? arrowOn : arrowOff})`}
                  style={{ transition: 'stroke .15s' }}
                />
              );
            })}
            {CHILD_NODES.map(({ key, x }) => {
              const focused = childrenActive && selection.focus === key;
              const stroke = !childrenActive ? alpha(theme.palette.text.primary, 0.12) : focused ? onStroke : alpha(onStroke, 0.55);
              return (
                <path
                  key={key}
                  d={childEdgePath(x)}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={focused ? 2.5 : childrenActive ? 1.5 : 1.2}
                  markerEnd={`url(#${childrenActive ? arrowOn : arrowOff})`}
                  style={{ transition: 'stroke .15s' }}
                />
              );
            })}
          </Box>

          {EDGES.map((e) => (
            <EdgeLabel
              key={`${e.from}-${e.to}`}
              x={e.labelX}
              y={e.labelY}
              text={e.card}
              active={e.from === selection.id || e.to === selection.id}
            />
          ))}
          <EdgeLabel x={500} y={350} text="1..* each" active={childrenActive} />

          {ENTITY_LIST.map((entity) => (
            <EntityNode
              key={entity.id}
              entity={entity}
              selected={selection.id === entity.id}
              near={related.has(entity.id)}
              onSelect={onSelect}
            />
          ))}
          {CHILD_NODES.map(({ key, label, sub, x }) => (
            <ChildNodeButton
              key={key}
              childKey={key}
              label={label}
              sub={sub}
              x={x}
              active={childrenActive}
              focused={childrenActive && selection.focus === key}
              onSelect={onSelect}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default memo(EntityMap);
